import React, { useState, useMemo } from 'react';
import { Save, Edit2, X, Copy, Check, Share2 } from 'lucide-react';

// Component imports
import PlatformTabs from './components/PlatformTabs';
import PostPreview from './components/PostPreview';

// Platform editor imports
import FacebookEditor from './platforms/FacebookEditor';
import InstagramEditor from './platforms/InstagramEditor';
import LinkedInEditor from './platforms/LinkedInEditor';
import TwitterEditor from './platforms/TwitterEditor';
import YouTubeEditor from './platforms/YouTubeEditor';
import GoogleBusinessEditor from './platforms/GoogleBusinessEditor';

// Type and config imports
import { PLATFORMS, PlatformKey } from './config/platformConfig';
import { PlatformContent, SocialMediaAsset } from './types/socialMedia.types';

interface SocialMediaPreviewProps {
  asset: SocialMediaAsset;
  apiBaseUrl?: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function SocialMediaPreview({ asset, apiBaseUrl = '', onSave }: SocialMediaPreviewProps) {
  // Parse metadata
  const parseMetadata = () => {
    if (asset.metadata) {
      if (typeof asset.metadata === 'string') {
        try {
          return JSON.parse(asset.metadata);
        } catch (e) {
          return {};
        }
      }
      return asset.metadata;
    }
    return {};
  };

  const initialMetadata = parseMetadata();

  // Extract text from platforms array if it exists (legacy format)
  const extractPlatformText = (platformName: string) => {
    if (initialMetadata.platforms && Array.isArray(initialMetadata.platforms)) {
      const platform = initialMetadata.platforms.find((p: any) => p.platform === platformName);
      if (platform) {
        return platform.content || platform.text || '';
      }
    }
    return '';
  };

  // Parse content - it might be JSON string with platform data
  const parseContent = () => {
    if (asset.content) {
      if (typeof asset.content === 'string') {
        try {
          const parsed = JSON.parse(asset.content);
          // Check if this is platform-specific content
          if (parsed.facebook || parsed.instagram || parsed.linkedin ||
              parsed.twitter || parsed.youtube || parsed.googlebusiness) {
            return parsed;
          }
        } catch (e) {
          // Not JSON, return as is
        }
      } else if (typeof asset.content === 'object') {
        // Already parsed object
        return asset.content;
      }
    }
    return null;
  };

  const contentData = parseContent();

  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformKey>('facebook');
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper function to filter out invalid blob URLs
  const filterValidMedia = (media: any[]) => {
    if (!Array.isArray(media)) return [];
    return media.filter(m => {
      // Filter out blob URLs as they are temporary and won't work after reload
      if (m.url && m.url.startsWith('blob:')) {
        return false;
      }
      return true;
    });
  };

  // Initialize platform content - prefer content over metadata
  const [platformContent, setPlatformContent] = useState<PlatformContent>({
    facebook: {
      text: contentData?.facebook?.text || initialMetadata.facebook?.text || extractPlatformText('facebook') || asset.content || '',
      postType: contentData?.facebook?.postType || initialMetadata.facebook?.postType || 'Text',
      link: contentData?.facebook?.link || initialMetadata.facebook?.link || '',
      callToAction: contentData?.facebook?.callToAction || initialMetadata.facebook?.callToAction || '',
      scheduledDate: contentData?.facebook?.scheduledDate || initialMetadata.facebook?.scheduledDate || '',
      scheduledTime: contentData?.facebook?.scheduledTime || initialMetadata.facebook?.scheduledTime || '',
      targeting: contentData?.facebook?.targeting || initialMetadata.facebook?.targeting || '',
      media: filterValidMedia(contentData?.facebook?.media || initialMetadata.facebook?.media || [])
    },
    instagram: {
      text: contentData?.instagram?.text || initialMetadata.instagram?.text || extractPlatformText('instagram') || asset.content || '',
      postType: contentData?.instagram?.postType || initialMetadata.instagram?.postType || 'Feed',
      hashtags: contentData?.instagram?.hashtags || initialMetadata.instagram?.hashtags || '',
      mentions: contentData?.instagram?.mentions || initialMetadata.instagram?.mentions || '',
      location: contentData?.instagram?.location || initialMetadata.instagram?.location || '',
      scheduledDate: contentData?.instagram?.scheduledDate || initialMetadata.instagram?.scheduledDate || '',
      scheduledTime: contentData?.instagram?.scheduledTime || initialMetadata.instagram?.scheduledTime || '',
      firstComment: contentData?.instagram?.firstComment || initialMetadata.instagram?.firstComment || '',
      media: filterValidMedia(contentData?.instagram?.media || initialMetadata.instagram?.media || [])
    },
    linkedin: {
      text: contentData?.linkedin?.text || initialMetadata.linkedin?.text || extractPlatformText('linkedin') || asset.content || '',
      postType: contentData?.linkedin?.postType || initialMetadata.linkedin?.postType || 'Text',
      headline: contentData?.linkedin?.headline || initialMetadata.linkedin?.headline || '',
      link: contentData?.linkedin?.link || initialMetadata.linkedin?.link || '',
      visibility: contentData?.linkedin?.visibility || initialMetadata.linkedin?.visibility || 'Public',
      scheduledDate: contentData?.linkedin?.scheduledDate || initialMetadata.linkedin?.scheduledDate || '',
      scheduledTime: contentData?.linkedin?.scheduledTime || initialMetadata.linkedin?.scheduledTime || '',
      targeting: contentData?.linkedin?.targeting || initialMetadata.linkedin?.targeting || '',
      media: filterValidMedia(contentData?.linkedin?.media || initialMetadata.linkedin?.media || [])
    },
    twitter: {
      text: contentData?.twitter?.text || initialMetadata.twitter?.text || extractPlatformText('twitter') || asset.content || '',
      postType: contentData?.twitter?.postType || initialMetadata.twitter?.postType || 'Tweet',
      hashtags: contentData?.twitter?.hashtags || initialMetadata.twitter?.hashtags || '',
      mentions: contentData?.twitter?.mentions || initialMetadata.twitter?.mentions || '',
      scheduledDate: contentData?.twitter?.scheduledDate || initialMetadata.twitter?.scheduledDate || '',
      scheduledTime: contentData?.twitter?.scheduledTime || initialMetadata.twitter?.scheduledTime || '',
      threadTexts: contentData?.twitter?.threadTexts || initialMetadata.twitter?.threadTexts || [],
      media: filterValidMedia(contentData?.twitter?.media || initialMetadata.twitter?.media || [])
    },
    youtube: {
      title: contentData?.youtube?.title || initialMetadata.youtube?.title || '',
      description: contentData?.youtube?.description || initialMetadata.youtube?.description || asset.content || '',
      postType: contentData?.youtube?.postType || initialMetadata.youtube?.postType || 'Video',
      tags: contentData?.youtube?.tags || initialMetadata.youtube?.tags || '',
      category: contentData?.youtube?.category || initialMetadata.youtube?.category || '',
      visibility: contentData?.youtube?.visibility || initialMetadata.youtube?.visibility || 'Public',
      scheduledDate: contentData?.youtube?.scheduledDate || initialMetadata.youtube?.scheduledDate || '',
      scheduledTime: contentData?.youtube?.scheduledTime || initialMetadata.youtube?.scheduledTime || '',
      thumbnail: contentData?.youtube?.thumbnail || initialMetadata.youtube?.thumbnail || '',
      media: filterValidMedia(contentData?.youtube?.media || initialMetadata.youtube?.media || [])
    },
    googlebusiness: {
      text: contentData?.googlebusiness?.text || initialMetadata.googlebusiness?.text || asset.content || '',
      postType: contentData?.googlebusiness?.postType || initialMetadata.googlebusiness?.postType || 'Update',
      callToAction: contentData?.googlebusiness?.callToAction || initialMetadata.googlebusiness?.callToAction || '',
      eventTitle: contentData?.googlebusiness?.eventTitle || initialMetadata.googlebusiness?.eventTitle || '',
      eventDate: contentData?.googlebusiness?.eventDate || initialMetadata.googlebusiness?.eventDate || '',
      eventTime: contentData?.googlebusiness?.eventTime || initialMetadata.googlebusiness?.eventTime || '',
      offerTitle: contentData?.googlebusiness?.offerTitle || initialMetadata.googlebusiness?.offerTitle || '',
      offerTerms: contentData?.googlebusiness?.offerTerms || initialMetadata.googlebusiness?.offerTerms || '',
      scheduledDate: contentData?.googlebusiness?.scheduledDate || initialMetadata.googlebusiness?.scheduledDate || '',
      scheduledTime: contentData?.googlebusiness?.scheduledTime || initialMetadata.googlebusiness?.scheduledTime || '',
      media: filterValidMedia(contentData?.googlebusiness?.media || initialMetadata.googlebusiness?.media || [])
    }
  });

  // Calculate character counts for all platforms
  const characterCounts = useMemo(() => {
    const counts: Record<PlatformKey, { current: number; limit: number }> = {} as any;

    Object.keys(PLATFORMS).forEach((key) => {
      const platform = key as PlatformKey;
      const content = platformContent[platform];
      let current = 0;
      let limit = 0;

      switch (platform) {
        case 'facebook':
          current = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;
          limit = PLATFORMS.facebook.limits.text!;
          break;
        case 'instagram':
          current = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;
          limit = PLATFORMS.instagram.limits.text!;
          break;
        case 'linkedin':
          current = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;
          limit = content.postType === 'Article'
            ? PLATFORMS.linkedin.limits.article!
            : PLATFORMS.linkedin.limits.text!;
          break;
        case 'twitter':
          current = (content.text || '').length;
          limit = PLATFORMS.twitter.limits.text!;
          break;
        case 'youtube':
          current = content.title.length;
          limit = PLATFORMS.youtube.limits.title!;
          break;
        case 'googlebusiness':
          current = (content.text || '').length;
          limit = PLATFORMS.googlebusiness.limits.text!;
          break;
      }

      counts[platform] = { current, limit };
    });

    return counts;
  }, [platformContent]);

  // Handle save
  const handleSave = async () => {
    if (!onSave) {
      alert('Save function not available');
      return;
    }

    setIsSaving(true);
    try {
      const updatedData = {
        content: platformContent[selectedPlatform].text ||
                 platformContent[selectedPlatform].description || '',
        metadata: {
          ...initialMetadata,
          ...platformContent
        }
      };

      await onSave(asset.id, JSON.stringify(updatedData));
      setIsEditing(false);
      alert('Content saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save content: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle copy
  const handleCopy = () => {
    const content = platformContent[selectedPlatform];
    const textContent = content.text || content.description || content.title || '';
    navigator.clipboard.writeText(textContent.replace(/<[^>]*>/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Update platform content
  const updatePlatformContent = (field: string, value: any) => {
    setPlatformContent(prev => ({
      ...prev,
      [selectedPlatform]: {
        ...prev[selectedPlatform],
        [field]: value
      }
    }));
  };

  // Render platform-specific editor
  const renderPlatformEditor = () => {
    const props = {
      content: platformContent[selectedPlatform],
      isEditing,
      onChange: updatePlatformContent
    };

    switch (selectedPlatform) {
      case 'facebook':
        return <FacebookEditor {...props} />;
      case 'instagram':
        return <InstagramEditor {...props} />;
      case 'linkedin':
        return <LinkedInEditor {...props} />;
      case 'twitter':
        return <TwitterEditor {...props} />;
      case 'youtube':
        return <YouTubeEditor {...props} />;
      case 'googlebusiness':
        return <GoogleBusinessEditor {...props} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Share2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Social Media Post Editor</h2>
            <span className="text-sm text-gray-500">{asset.file_name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                <span className="text-sm">Edit Post</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Platform Tabs */}
      <PlatformTabs
        selectedPlatform={selectedPlatform}
        onPlatformChange={setSelectedPlatform}
        characterCounts={characterCounts}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor Panel */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <img
                  src={PLATFORMS[selectedPlatform].icon}
                  alt={PLATFORMS[selectedPlatform].name}
                  className="w-6 h-6 mr-2"
                />
                {PLATFORMS[selectedPlatform].name} Settings
              </h3>

              <div className="space-y-4">
                {renderPlatformEditor()}
              </div>
            </div>

            {/* Preview Panel */}
            <div className="space-y-4">
              <PostPreview
                platform={selectedPlatform}
                content={platformContent[selectedPlatform]}
              />

              {/* Platform Limits Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  {PLATFORMS[selectedPlatform].name} Content Limits
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                  {Object.entries(PLATFORMS[selectedPlatform].limits).map(([key, limit]) => (
                    <div key={key} className="flex justify-between">
                      <span className="capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                      </span>
                      <span className="font-semibold">
                        {typeof limit === 'number' ? limit.toLocaleString() : limit}
                        {key !== 'thread' && key !== 'hashtags' && key !== 'mentions' && ' chars'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}